package com.schoolconnect.app.feature.quickannouncements

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.list.AdminStatusChip
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.info_circle
import org.jetbrains.compose.resources.painterResource
import kotlinx.coroutines.delay

@Composable
internal fun EditQuickAnnouncementDialog(
    row: QuickAnnouncementRow?,
    onDismiss: () -> Unit,
    onSubmit: (String, QuickAnnouncementDraft) -> Unit,
    isSaving: Boolean = false
) {
    if (row == null) return

    var announcement by remember(row.id) { mutableStateOf(row.message) }
    var displayUntil by remember(row.id) { mutableStateOf(row.displayUntil.substringBefore(",").trim()) }
    var displayTime by remember(row.id) { mutableStateOf(row.displayUntil.toTwentyFourHourTime()) }
    var submitted by remember(row.id) { mutableStateOf(false) }
    var pendingSubmit by remember(row.id) { mutableStateOf<QuickAnnouncementDraft?>(null) }
    val minimumDisplayDate = currentAppDateLabel()

    LaunchedEffect(pendingSubmit) {
        pendingSubmit?.let {
            delay(350)
            onSubmit(row.id, it)
            pendingSubmit = null
        }
    }

    val announcementError = if (submitted && announcement.isBlank()) "Announcement is required." else ""
    val dateError = when {
        submitted && displayUntil.isBlank() -> "Display until date is required."
        submitted && appDateInputComparable(displayUntil) == null -> "Enter a valid display until date."
        submitted && appDateInputComparable(displayUntil)!! < appDateInputComparable(minimumDisplayDate)!! -> "Display until date cannot be earlier than today."
        else -> ""
    }
    val timeError = if (submitted && displayTime.isBlank()) "Display until time is required." else ""
    val hasMissingRequiredFields = announcement.isBlank() || dateError.isNotBlank() || displayTime.isBlank()

    fun save(publish: Boolean) {
        submitted = true
        if (!hasMissingRequiredFields) pendingSubmit = QuickAnnouncementDraft(announcement, displayUntil, displayTime, publish)
    }

    AppDialogScaffold(
        onDismiss = { if (!isSaving) onDismiss() },
        size = AppDialogSize.Wide,
        dialogWidthOverride = 1120.dp,
        maxDialogHeight = 900.dp,
        isLoading = isSaving,
        loadingTitle = if (pendingSubmit?.publish == true) "Publishing announcement" else "Saving announcement"
    ) {
        AppDialogHeader(
            title = "Edit Announcement",
            subtitle = "Update the announcement displayed to all parents.",
            onClose = { if (!isSaving) onDismiss() },
            colors = AppDialogDefaultColors
        )
        AppDialogValidationSummary(
            errors = listOf(announcementError, dateError, timeError),
            modifier = Modifier.padding(horizontal = 28.dp, vertical = 8.dp)
        )
        AppDialogBody(
            compactBreakpoint = 820.dp,
            horizontalPadding = 28.dp,
            verticalPadding = 20.dp
        ) { compact ->
            if (compact) {
                AppDialogFieldColumn("QA ID", colors = AppDialogDefaultColors) {
                    AnnouncementIdField(row.id)
                }
                StatusField(row.status)
                AppDialogFieldColumn("Display Until", required = true, error = dateError, colors = AppDialogDefaultColors) {
                    AppDialogDateField(displayUntil, { displayUntil = it }, error = dateError, colors = AppDialogDefaultColors, minimumDate = minimumDisplayDate)
                }
                AppDialogFieldColumn("Time", required = true, error = timeError, colors = AppDialogDefaultColors) {
                    AppDialogTimeField(displayTime, { displayTime = it }, error = timeError, colors = AppDialogDefaultColors)
                }
            } else {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(22.dp), verticalAlignment = Alignment.Bottom) {
                    AppDialogFieldColumn("QA ID", modifier = Modifier.weight(1.35f), colors = AppDialogDefaultColors) {
                        AnnouncementIdField(row.id)
                    }
                    Box(Modifier.weight(.65f).padding(bottom = 8.dp), contentAlignment = Alignment.CenterStart) {
                        StatusField(row.status)
                    }
                    AppDialogFieldColumn("Display Until", modifier = Modifier.weight(1.1f), colors = AppDialogDefaultColors) {
                        AppDialogDateField(displayUntil, { displayUntil = it }, error = dateError, colors = AppDialogDefaultColors, minimumDate = minimumDisplayDate)
                    }
                    AppDialogFieldColumn("Time", modifier = Modifier.weight(.9f), colors = AppDialogDefaultColors) {
                        AppDialogTimeField(displayTime, { displayTime = it }, error = timeError, colors = AppDialogDefaultColors)
                    }
                }
            }

            AppDialogFieldColumn(
                label = "Announcement",
                required = true,
                error = announcementError,
                colors = AppDialogDefaultColors
            ) {
                AppDialogTextField(
                    value = announcement,
                    onValueChange = { announcement = it },
                    placeholder = "Enter a short announcement",
                    colors = AppDialogDefaultColors,
                    error = announcementError,
                    singleLine = false,
                    fieldHeight = 160.dp,
                    maxLength = 200,
                    trailingIcon = { AppText("${announcement.length} / 200", color = AppMuted, style = androidx.compose.material3.MaterialTheme.typography.labelSmall) }
                )
            }

            Row(
                Modifier
                    .fillMaxWidth()
                    .background(AppInfoBg, RoundedCornerShape(8.dp))
                    .border(1.dp, AppInfoBorder, RoundedCornerShape(8.dp))
                    .padding(horizontal = 14.dp, vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                Icon(painterResource(Res.drawable.info_circle), "Information", tint = AppInfoText, modifier = Modifier.size(20.dp))
                AppText("Publishing will make this announcement active immediately. It will remain visible to all parents until the selected date and time.", color = AppNavy, style = androidx.compose.material3.MaterialTheme.typography.bodyMedium)
            }
        }
        AppDialogDivider()
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction("Cancel", { if (!isSaving) onDismiss() }, enabled = !isSaving),
                AppDialogFooterAction(if (isSaving) "Saving..." else "Save Changes", { save(false) }, width = 170.dp, enabled = !isSaving),
                AppDialogFooterAction(if (isSaving) "Publishing..." else "Publish", { save(true) }, style = AppDialogActionStyle.Primary, enabled = !isSaving)
            ),
            leadingContent = {
                AppDialogFooterButton(
                    action = AppDialogFooterAction("Preview", onClick = {}, width = 140.dp, enabled = !isSaving),
                    modifier = Modifier.width(140.dp)
                )
            }
        )
    }
}

@Composable
private fun AnnouncementIdField(id: String) {
    AppDialogTextField(
        value = id,
        onValueChange = {},
        placeholder = "QA ID",
        readOnly = true,
        colors = AppDialogDefaultColors
    )
}

@Composable
private fun StatusField(status: String) {
    AdminStatusChip(
        label = status,
        contentColor = AppPrimary,
        backgroundColor = AppPrimarySoft,
        modifier = Modifier.padding(bottom = 2.dp)
    )
}

private fun String.toTwentyFourHourTime(): String {
    val timeWithMeridiem = substringAfter(",", "").trim()
    val pieces = timeWithMeridiem.split(" ")
    if (pieces.size < 2) return timeWithMeridiem
    val clock = pieces[0].split(":")
    if (clock.size != 2) return timeWithMeridiem
    val hour = clock[0].toIntOrNull() ?: return timeWithMeridiem
    val minute = clock[1].toIntOrNull() ?: return timeWithMeridiem
    val normalizedHour = when {
        pieces[1].equals("PM", true) && hour < 12 -> hour + 12
        pieces[1].equals("AM", true) && hour == 12 -> 0
        else -> hour
    }
    return "${normalizedHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}"
}
